import { useState, useEffect, useCallback } from 'react';
import { User, Search, Filter, AlertCircle, UserCheck, History, PlusCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../utils/supabaseStorage';
import { parseAdminStaffSession } from '../utils/adminSession';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { AdminPageToolbar } from '../components/admin/AdminLayout';
import type { AppUserRole } from '../types/roles';

interface UserRow {
  hy_user_id: string;
  hy_name: string;
  email: string | null;
  role: AppUserRole;
  credit_balance: number;
  created_at: string;
  updated_at: string;
}

type FilterType = 'all' | 'superadmin' | 'admin' | 'creator' | 'user';

type CreditEntryKind = 'admin_grant' | 'giveaway';

interface CreditLedgerRow {
  id: string;
  hy_user_id: string;
  delta: number;
  balance_after: number | null;
  reason: string;
  ref: string | null;
  created_at: string;
  entry_kind: string;
  actor_hy_user_id: string | null;
  metadata: Record<string, unknown> | null;
}

export const UsersPage = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState<UserRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState<FilterType>('all');
  const [staffSession, setStaffSession] = useState<ReturnType<typeof parseAdminStaffSession>>(null);
  const [editing, setEditing] = useState<UserRow | null>(null);
  const [editRole, setEditRole] = useState<AppUserRole>('user');
  const [saving, setSaving] = useState(false);

  const [creditUser, setCreditUser] = useState<UserRow | null>(null);
  const [grantAmount, setGrantAmount] = useState('');
  const [grantKind, setGrantKind] = useState<CreditEntryKind>('admin_grant');
  const [grantReason, setGrantReason] = useState('');
  const [grantRef, setGrantRef] = useState('');
  const [granting, setGranting] = useState(false);

  const [historyUser, setHistoryUser] = useState<UserRow | null>(null);
  const [ledgerRows, setLedgerRows] = useState<CreditLedgerRow[]>([]);
  const [ledgerLoading, setLedgerLoading] = useState(false);

  const isSuperadmin = staffSession?.role === 'superadmin';

  const loadStaff = useCallback(() => {
    setStaffSession(parseAdminStaffSession());
  }, []);

  const loadUsers = useCallback(async () => {
    if (!isSuperadmin) return;
    try {
      setLoading(true);
      setError(null);
      const { data, error: fetchError } = await supabase
        .from('users')
        .select('hy_user_id, hy_name, email, role, credit_balance, created_at, updated_at')
        .order('updated_at', { ascending: false });

      if (fetchError) throw fetchError;
      setUsers((data as UserRow[]) || []);
    } catch (err) {
      console.error(err);
      setError(err instanceof Error ? err.message : 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [isSuperadmin]);

  useEffect(() => {
    loadStaff();
  }, [loadStaff]);

  useEffect(() => {
    void loadUsers();
  }, [loadUsers]);

  const goBack = () => {
    navigate('/admin');
  };

  const openEdit = (u: UserRow) => {
    setEditing(u);
    setEditRole(u.role);
    setError(null);
  };

  const saveRole = async () => {
    if (!editing) return;
    try {
      setSaving(true);
      setError(null);
      const { error: upErr } = await supabase
        .from('users')
        .update({ role: editRole, updated_at: new Date().toISOString() })
        .eq('hy_user_id', editing.hy_user_id);
      if (upErr) throw upErr;
      setSuccess('Role updated');
      setEditing(null);
      await loadUsers();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Update failed');
    } finally {
      setSaving(false);
    }
  };

  const openGrantCredits = (u: UserRow) => {
    setCreditUser(u);
    setGrantAmount('');
    setGrantKind('admin_grant');
    setGrantReason('');
    setGrantRef('');
    setError(null);
  };

  const saveGrantCredits = async () => {
    if (!creditUser) return;
    const n = Number.parseInt(grantAmount, 10);
    if (!Number.isFinite(n) || n <= 0) {
      setError('Enter a positive whole number of credits.');
      return;
    }
    const reason = grantReason.trim();
    if (!reason) {
      setError('Add a short note for the ledger (reason).');
      return;
    }
    try {
      setGranting(true);
      setError(null);
      const { data, error: rpcErr } = await supabase.rpc('superadmin_add_credits', {
        p_target_hy_user_id: creditUser.hy_user_id,
        p_amount: n,
        p_reason: reason,
        p_ref: grantRef.trim() || null,
        p_entry_kind: grantKind,
      });
      if (rpcErr) throw rpcErr;
      const row = data as { ok?: boolean; error?: string; current_credit?: number } | null;
      if (!row?.ok) {
        const code = row?.error || 'grant_failed';
        const msg =
          code === 'forbidden'
            ? 'Not allowed.'
            : code === 'user_not_found'
              ? 'User not found.'
              : code === 'invalid_entry_kind'
                ? 'Invalid entry type.'
                : code === 'invalid_amount'
                  ? 'Invalid amount.'
                  : code;
        setError(msg);
        return;
      }
      setSuccess(`Added ${n} credits to ${creditUser.hy_name}. New balance: ${row.current_credit ?? '—'}.`);
      setCreditUser(null);
      await loadUsers();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Grant failed');
    } finally {
      setGranting(false);
    }
  };

  const openHistory = async (u: UserRow) => {
    setHistoryUser(u);
    setLedgerRows([]);
    setError(null);
    try {
      setLedgerLoading(true);
      const { data, error: qErr } = await supabase
        .from('credit_ledger')
        .select(
          'id, hy_user_id, delta, balance_after, reason, ref, created_at, entry_kind, actor_hy_user_id, metadata',
        )
        .eq('hy_user_id', u.hy_user_id)
        .order('created_at', { ascending: false })
        .limit(100);
      if (qErr) throw qErr;
      setLedgerRows((data as CreditLedgerRow[]) || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load credit history');
      setHistoryUser(null);
    } finally {
      setLedgerLoading(false);
    }
  };

  const filteredUsers = users.filter((user) => {
    const matchesSearch =
      !searchTerm.trim() ||
      user.hy_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.hy_user_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (user.email && user.email.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesFilter =
      filter === 'all' ||
      (filter === 'superadmin' && user.role === 'superadmin') ||
      (filter === 'admin' && user.role === 'admin') ||
      (filter === 'creator' && user.role === 'creator') ||
      (filter === 'user' && user.role === 'user');

    return matchesSearch && matchesFilter;
  });

  const filterOptions = [
    { id: 'all' as const, label: 'All', count: users.length },
    { id: 'superadmin' as const, label: 'Superadmin', count: users.filter((u) => u.role === 'superadmin').length },
    { id: 'admin' as const, label: 'Admin', count: users.filter((u) => u.role === 'admin').length },
    { id: 'creator' as const, label: 'Creator', count: users.filter((u) => u.role === 'creator').length },
    { id: 'user' as const, label: 'User', count: users.filter((u) => u.role === 'user').length },
  ];

  const roleBadge = (role: string) => {
    if (role === 'superadmin')
      return { label: 'Superadmin', color: 'text-status-error bg-status-error/10 border-status-error/30' };
    if (role === 'admin')
      return { label: 'Admin', color: 'text-status-warning bg-status-warning/10 border-status-warning/30' };
    if (role === 'creator')
      return { label: 'Creator', color: 'text-brand bg-brand/10 border-brand/30' };
    return { label: 'User', color: 'text-th-secondary bg-elevated border-subtle' };
  };

  if (!isSuperadmin) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-6 text-center">
        <p className="text-th-secondary max-w-md">
          User management (roles) is limited to superadmin accounts. Sign in with a superadmin profile or go back to the
          dashboard.
        </p>
        <button type="button" className="mt-6 button-secondary" onClick={goBack}>
          Back
        </button>
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col bg-base">
      <AdminPageToolbar
        title="Users & roles"
        subtitle="Superadmin: roles, credit grants, and ledger history"
      />

      <main className="min-h-0 flex-1 overflow-y-auto max-w-6xl mx-auto px-4 py-6 w-full space-y-6">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-th-secondary" />
            <input
              type="text"
              placeholder="Search by name, email, or id…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 bg-elevated border border-subtle rounded-lg text-th-primary"
            />
          </div>
          <Filter className="w-4 h-4 text-th-secondary shrink-0" />
          {filterOptions.map((opt) => (
            <button
              key={opt.id}
              type="button"
              onClick={() => setFilter(opt.id)}
              className={`px-3 py-1.5 rounded-full text-sm ${
                filter === opt.id ? 'bg-brand text-surface' : 'bg-elevated border border-subtle text-th-secondary'
              }`}
            >
              {opt.label} ({opt.count})
            </button>
          ))}
        </div>

        {success && (
          <div className="p-4 bg-status-success/10 border border-status-success/30 rounded-lg flex items-center gap-2 text-status-success">
            <UserCheck className="w-5 h-5" />
            {success}
            <button type="button" className="ml-auto text-sm underline" onClick={() => setSuccess(null)}>
              Dismiss
            </button>
          </div>
        )}
        {error && (
          <div className="p-4 bg-status-error/10 border border-status-error/30 rounded-lg flex items-center gap-2 text-status-error">
            <AlertCircle className="w-5 h-5" />
            {error}
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-16">
            <LoadingSpinner size="lg" />
          </div>
        ) : (
          <div className="space-y-3">
            {filteredUsers.map((u) => {
              const ri = roleBadge(u.role);
              return (
                <div key={u.hy_user_id} className="card p-4 flex flex-wrap items-center justify-between gap-4">
                  <div className="flex items-start gap-3 min-w-0">
                    <div className="w-10 h-10 bg-brand rounded-full flex items-center justify-center shrink-0">
                      <User className="w-5 h-5 text-white" />
                    </div>
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="font-semibold text-th-primary truncate">{u.hy_name}</h3>
                        <span className={`text-xs px-2 py-0.5 rounded-full border ${ri.color}`}>{ri.label}</span>
                      </div>
                      <p className="text-xs text-th-secondary font-mono truncate mt-1">{u.hy_user_id}</p>
                      {u.email ? <p className="text-xs text-th-tertiary truncate">{u.email}</p> : null}
                      <p className="text-xs text-th-tertiary mt-1">Credits: {u.credit_balance}</p>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2 shrink-0 justify-end">
                    <button
                      type="button"
                      className="button-secondary text-sm inline-flex items-center gap-1.5"
                      onClick={() => openGrantCredits(u)}
                    >
                      <PlusCircle className="w-4 h-4" />
                      Add credits
                    </button>
                    <button
                      type="button"
                      className="button-secondary text-sm inline-flex items-center gap-1.5"
                      onClick={() => void openHistory(u)}
                    >
                      <History className="w-4 h-4" />
                      History
                    </button>
                    <button type="button" className="button-secondary text-sm" onClick={() => openEdit(u)}>
                      Change role
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>

      {editing && (
        <div className="fixed inset-0 bg-black/80 z-modal-content flex items-center justify-center p-4">
          <div className="bg-surface border border-subtle rounded-2xl w-full max-w-md p-6 space-y-4">
            <h2 className="text-lg font-bold text-th-primary">Role for {editing.hy_name}</h2>
            <select
              value={editRole}
              onChange={(e) => setEditRole(e.target.value as AppUserRole)}
              className="w-full bg-elevated border border-subtle rounded-lg px-3 py-2 text-th-primary"
            >
              <option value="user">user</option>
              <option value="creator">creator</option>
              <option value="admin">admin</option>
              <option value="superadmin">superadmin</option>
            </select>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" className="button-secondary" onClick={() => setEditing(null)} disabled={saving}>
                Cancel
              </button>
              <button type="button" className="button-primary" onClick={() => void saveRole()} disabled={saving}>
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {creditUser && (
        <div className="fixed inset-0 bg-black/80 z-modal-content flex items-center justify-center p-4">
          <div className="bg-surface border border-subtle rounded-2xl w-full max-w-md p-6 space-y-4 max-h-[90vh] overflow-y-auto">
            <h2 className="text-lg font-bold text-th-primary">Add credits — {creditUser.hy_name}</h2>
            <p className="text-xs text-th-secondary font-mono break-all">{creditUser.hy_user_id}</p>
            <label className="block space-y-1">
              <span className="text-sm text-th-secondary">Amount</span>
              <input
                type="number"
                min={1}
                step={1}
                value={grantAmount}
                onChange={(e) => setGrantAmount(e.target.value)}
                className="w-full bg-elevated border border-subtle rounded-lg px-3 py-2 text-th-primary"
                placeholder="e.g. 100"
              />
            </label>
            <label className="block space-y-1">
              <span className="text-sm text-th-secondary">Entry type</span>
              <select
                value={grantKind}
                onChange={(e) => setGrantKind(e.target.value as CreditEntryKind)}
                className="w-full bg-elevated border border-subtle rounded-lg px-3 py-2 text-th-primary"
              >
                <option value="admin_grant">Admin grant</option>
                <option value="giveaway">Giveaway</option>
              </select>
            </label>
            <label className="block space-y-1">
              <span className="text-sm text-th-secondary">Note (ledger reason)</span>
              <textarea
                value={grantReason}
                onChange={(e) => setGrantReason(e.target.value)}
                rows={3}
                className="w-full bg-elevated border border-subtle rounded-lg px-3 py-2 text-th-primary resize-y min-h-[4rem]"
                placeholder="Visible in audit history"
              />
            </label>
            <label className="block space-y-1">
              <span className="text-sm text-th-secondary">External ref (optional)</span>
              <input
                type="text"
                value={grantRef}
                onChange={(e) => setGrantRef(e.target.value)}
                className="w-full bg-elevated border border-subtle rounded-lg px-3 py-2 text-th-primary"
                placeholder="Invoice id, ticket, etc."
              />
            </label>
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                className="button-secondary"
                onClick={() => setCreditUser(null)}
                disabled={granting}
              >
                Cancel
              </button>
              <button type="button" className="button-primary" onClick={() => void saveGrantCredits()} disabled={granting}>
                {granting ? 'Granting…' : 'Grant credits'}
              </button>
            </div>
          </div>
        </div>
      )}

      {historyUser && (
        <div className="fixed inset-0 bg-black/80 z-modal-content flex items-center justify-center p-4">
          <div className="bg-surface border border-subtle rounded-2xl w-full max-w-4xl p-6 space-y-4 max-h-[90vh] flex flex-col">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div>
                <h2 className="text-lg font-bold text-th-primary">Credit history — {historyUser.hy_name}</h2>
                <p className="text-xs text-th-secondary font-mono break-all mt-1">{historyUser.hy_user_id}</p>
              </div>
              <button type="button" className="button-secondary text-sm shrink-0" onClick={() => setHistoryUser(null)}>
                Close
              </button>
            </div>
            {ledgerLoading ? (
              <div className="flex justify-center py-12">
                <LoadingSpinner size="lg" />
              </div>
            ) : ledgerRows.length === 0 ? (
              <p className="text-th-secondary text-sm py-8 text-center">No ledger entries yet.</p>
            ) : (
              <div className="min-h-0 flex-1 overflow-auto rounded-lg border border-subtle">
                <table className="w-full text-sm text-left">
                  <thead className="bg-elevated sticky top-0 text-th-secondary">
                    <tr>
                      <th className="p-2 font-medium">When</th>
                      <th className="p-2 font-medium">Δ</th>
                      <th className="p-2 font-medium">Balance</th>
                      <th className="p-2 font-medium">Kind</th>
                      <th className="p-2 font-medium">Reason</th>
                      <th className="p-2 font-medium">Ref</th>
                      <th className="p-2 font-medium">Actor</th>
                    </tr>
                  </thead>
                  <tbody className="text-th-primary divide-y divide-subtle">
                    {ledgerRows.map((r) => (
                      <tr key={r.id} className="align-top">
                        <td className="p-2 whitespace-nowrap text-xs">
                          {new Date(r.created_at).toLocaleString()}
                        </td>
                        <td className={`p-2 font-mono ${r.delta >= 0 ? 'text-status-success' : 'text-status-error'}`}>
                          {r.delta >= 0 ? `+${r.delta}` : r.delta}
                        </td>
                        <td className="p-2 font-mono text-xs">{r.balance_after ?? '—'}</td>
                        <td className="p-2 text-xs">{r.entry_kind}</td>
                        <td className="p-2 text-xs max-w-[200px] break-words">{r.reason}</td>
                        <td className="p-2 text-xs font-mono max-w-[120px] truncate" title={r.ref ?? ''}>
                          {r.ref ?? '—'}
                        </td>
                        <td className="p-2 text-xs font-mono max-w-[100px] truncate" title={r.actor_hy_user_id ?? ''}>
                          {r.actor_hy_user_id ?? '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
